import { useState, type FormEvent } from "react";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

function deriveCompanyName(rawUrl: string): string {
  try {
    const host = new URL(rawUrl).hostname.replace(/^www\./, "");
    const root = host.split(".")[0] ?? host;
    return root.charAt(0).toUpperCase() + root.slice(1);
  } catch {
    return "Audit";
  }
}

function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export interface AuditFormProps {
  /** "hero" = compact inline form on dark hero; "card" = embedded in CTA card. */
  variant: "hero" | "card";
  /** Optional form id — used to scroll to it from buttons elsewhere on the page. */
  id?: string;
  /** UTM context — passed through to engagement provisioning for attribution. */
  source?: string;
}

export function AuditForm({ variant, id, source }: AuditFormProps) {
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus({ kind: "submitting" });

    const targetUrl = normalizeUrl(url);
    const name = deriveCompanyName(targetUrl);

    try {
      const res = await fetch("/api/engagements/provision", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          targetUrl,
          prospectEmail: email.trim(),
          kind: "client",
          mode: "dry-run-audit",
          ...(source ? { source } : {}),
        }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      setStatus({ kind: "success" });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Erreur réseau",
      });
    }
  }

  if (status.kind === "success") {
    return (
      <div
        id={id}
        className="p-6 rounded-2xl bg-blue-500/10 border border-blue-500/30 text-left max-w-xl mx-auto"
      >
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-white font-semibold mb-1">Audit lancé.</p>
            <p className="text-slate-300 text-sm leading-relaxed">
              Votre rapport personnalisé arrive dans votre boîte mail sous{" "}
              <strong className="text-white">2 à 3 minutes</strong>, signé{" "}
              <code className="text-blue-300 font-mono text-xs bg-slate-800/60 px-1.5 py-0.5 rounded">
                audit@agents.levier-ia.fr
              </code>
              . Pensez à vérifier vos spams.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const inputBase =
    "w-full px-4 py-3 text-base text-white bg-slate-900/80 border border-slate-700 rounded-xl placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50";

  const buttonBase =
    "inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-500 transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0";

  return (
    <form
      id={id}
      onSubmit={onSubmit}
      className={
        variant === "hero"
          ? "w-full max-w-xl mx-auto space-y-3"
          : "max-w-xl mx-auto mb-8 space-y-3"
      }
    >
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={status.kind === "submitting"}
          placeholder="https://votre-site.fr"
          aria-label="URL de votre site"
          className={`flex-1 ${inputBase}`}
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status.kind === "submitting"}
          placeholder="votre@email.fr"
          aria-label="Email professionnel"
          className={`flex-1 ${inputBase}`}
        />
      </div>
      <button
        type="submit"
        disabled={status.kind === "submitting"}
        className={`w-full sm:w-auto ${buttonBase}`}
      >
        {status.kind === "submitting" ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Lancement de l'agent…
          </>
        ) : (
          <>
            Lancer mon audit gratuit
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </>
        )}
      </button>
      {status.kind === "error" && (
        <p className="text-sm text-red-400 mt-2">
          Erreur : {status.message}. Réessayez ou écrivez à{" "}
          <a href="mailto:contact@levier-ia.fr" className="underline">
            contact@levier-ia.fr
          </a>
          .
        </p>
      )}
    </form>
  );
}
